/**
 * Enhanced Tooltip Component
 *
 * This implementation addresses the issue with Radix UI Tooltip not working properly on touch devices.
 * Radix Tooltip is designed to work with hover events, which don't exist on touch devices.
 *
 * Problem:
 * - On desktop: Tooltip works as expected with hover.
 * - On mobile/touch: Tooltip doesn't show up because there's no hover event.
 *
 * Solution:
 * This implementation provides a dual behavior based on device capabilities:
 * 1. On devices with hover support (desktop/laptop):
 *    - Maintain the original hover behavior
 *    - Use standard Radix UI components
 *
 * 2. On touch devices (mobile/tablets):
 *    - Enable click/tap to toggle tooltip visibility
 *    - Clicking on the tooltip content will close it
 *    - Uses a context to manage open/close state
 *
 * The implementation detects device capabilities using the `hover: hover` media query
 * and branches the component behavior accordingly, providing a seamless experience
 * across different devices without compromising functionality.
 *
 * Based on workaround from: https://github.com/radix-ui/primitives/issues/955#issuecomment-960610209
 */
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { getShadowPortalContainer } from 'common-utils';
import React, { useLayoutEffect, useState } from 'react';
import { noop } from 'remeda';

import { styled, theme } from '../../stitches.config';
import { rem } from '../../util';

type TooltipTriggerContextType = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  supportMobileTap?: boolean;
};

export const TooltipTriggerContext =
  React.createContext<TooltipTriggerContextType>({
    open: false,
    setOpen: noop,
    supportMobileTap: true,
  });

function useHasHover() {
  try {
    return matchMedia('(hover: hover)').matches;
  } catch {
    const minDesktopWidth = 768;
    if (typeof window !== 'undefined') {
      return window.innerWidth >= minDesktopWidth;
    }
    return true;
  }
}

export const TooltipProvider = RadixTooltip.Provider;

export const TooltipRoot: React.FC<
  RadixTooltip.TooltipProps & { supportMobileTap?: boolean }
> = ({ children, supportMobileTap = true, ...props }) => {
  const [open, setOpen] = React.useState<boolean>(props.defaultOpen ?? false);
  const hasHover = useHasHover();

  if (hasHover || !supportMobileTap) {
    return (
      <RadixTooltip.Root {...props}>
        <TooltipTriggerContext.Provider
          value={{ open, setOpen, supportMobileTap }}
        >
          {children}
        </TooltipTriggerContext.Provider>
      </RadixTooltip.Root>
    );
  }

  return (
    <RadixTooltip.Root
      {...props}
      delayDuration={0}
      onOpenChange={setOpen}
      open={open}
    >
      <TooltipTriggerContext.Provider
        value={{ open, setOpen, supportMobileTap }}
      >
        {children}
      </TooltipTriggerContext.Provider>
    </RadixTooltip.Root>
  );
};

export const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof StyledTooltipTrigger>,
  React.ComponentPropsWithoutRef<typeof StyledTooltipTrigger>
>(({ children, ...props }, ref) => {
  const hasHover = useHasHover();
  const { open, setOpen, supportMobileTap } = React.useContext(
    TooltipTriggerContext,
  );

  if (hasHover || !supportMobileTap) {
    return (
      <StyledTooltipTrigger ref={ref} {...props}>
        {children}
      </StyledTooltipTrigger>
    );
  }

  return (
    <StyledTooltipTrigger
      ref={ref}
      {...props}
      onClick={(e) => {
        const isLink =
          e.currentTarget.hasAttribute('href') ||
          e.currentTarget.querySelector('a[href]');

        if (isLink) {
          if (props.onClick) {
            props.onClick(e);
          }
          return;
        }
        e.preventDefault();
        if (open) {
          setOpen(false);
        } else if (!props.onClick) {
          setOpen(true);
        }
      }}
      onFocus={(e) => {
        e.preventDefault();
        setTimeout(() => setOpen(true), 0);
      }}
      onBlur={(e) => {
        e.preventDefault();
        setOpen(false);
      }}
    >
      {children}
    </StyledTooltipTrigger>
  );
});

const StyledTooltipTrigger = styled(RadixTooltip.Trigger);

const StyledTooltipContent = styled(RadixTooltip.Content, {
  position: 'relative',
  zIndex: 2147483647, //to support shadowdom in case of extensions
  backgroundColor: theme.colors.blue12,
  color: theme.colors.gray1,
  padding: rem(4),
  fontSize: rem(14),
  lineHeight: rem(16),
  borderRadius: rem(4),
  variants: {
    size: {
      big: {
        padding: rem(8),
        fontSize: rem(16),
        fontWeight: theme.fontWeights.medium,
      },
    },
    customStyled: {
      true: {
        borderRadius: rem(10),
        border: 'none',
        backgroundColor: theme.colors.gray11,
        color: theme.colors.gray1,
        fontSize: rem(14),
        lineHeight: rem(20),
        fontWeight: 500,
        padding: rem(8),
        maxWidth: rem(300),
        wordBreak: 'break-word',
      },
    },
    theme: {
      'gray-card': {
        backgroundColor: theme.colors.gray1,
        borderRadius: '10px',
        color: theme.colors.blue12,
        lineHeight: '17px',
        fontWeight: theme.fontWeights.normal,
        padding: '10px',
        width: '100%',
        maxWidth: '320px',
        filter: 'drop-shadow(rgba(0, 0, 0, 0.2) 0px 4px 24px)',
      },
    },
  },
});

export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof StyledTooltipContent>,
  React.ComponentPropsWithoutRef<typeof StyledTooltipContent>
>(({ children, ...props }, ref) => {
  const hasHover = useHasHover();
  const { setOpen } = React.useContext(TooltipTriggerContext);

  if (hasHover) {
    return (
      <StyledTooltipContent ref={ref} {...props}>
        {children}
      </StyledTooltipContent>
    );
  }

  return (
    <StyledTooltipContent
      ref={ref}
      {...props}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(false);
      }}
    >
      {children}
    </StyledTooltipContent>
  );
});

export const TooltipPortal: React.FC<
  React.ComponentPropsWithoutRef<typeof RadixTooltip.Portal>
> = ({ ...props }) => {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );

  useLayoutEffect(() => {
    const container = getShadowPortalContainer();

    if (container) {
      setPortalContainer(container);
    }
  }, []);

  return (
    <RadixTooltip.Portal
      {...props}
      container={portalContainer ? portalContainer : undefined}
    />
  );
};

export const TooltipArrow = styled(RadixTooltip.Arrow, {
  fill: theme.colors.blue12,
  variants: {
    customStyled: {
      true: {
        fill: theme.colors.gray11,
      },
    },
  },
});
